import type { Address } from "viem"
import { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Link } from "react-router-dom"

import type { ManualClPoolStore } from "@/domains/thick-v1/manual-cl-store"
import type { ManualClPositionStore } from "@/domains/thick-v1/positions"
import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { LcAddress } from "@/domains/core"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"
import { IndexerFreshnessBanner } from "@/domains/thick-v1/components/indexer-freshness-banner"
import { PoolActivityFeedView } from "@/domains/thick-v1/components/pool-detail/pool-activity-feed-view"
import { PoolDetailHeaderView } from "@/domains/thick-v1/components/pool-detail/pool-detail-header-view"
import { PoolMetricRowView } from "@/domains/thick-v1/components/pool-detail/pool-metric-row-view"
import { PoolOhlcChartView } from "@/domains/thick-v1/components/pool-detail/pool-ohlc-chart-view"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

/**
 * Top-level composite for the Manual CL pool detail page. Binds the shared
 * {@link ManualClPoolStore} (plus the two on-demand {@link PoolOhlcStore}
 * and {@link PoolActivityStore} instances) to the view components.
 *
 * Lifecycle — the single `useEffect` below is a pure "route → store" fan-out:
 * it pushes `poolAddress` into three stores and clears them on unmount. No
 * data fetching happens here; the orchestrator
 * reacts to `setActiveDetailPoolAddress` by issuing the indexer query and
 * owns the `isActiveDetailPoolResolving` flag the UI observes below.
 */
export interface PoolDetailPageCompositeProps {
  poolAddress: Address
}

export const PoolDetailPageComposite = observer(function PoolDetailPageComposite({
  poolAddress,
}: PoolDetailPageCompositeProps) {
  const scaleApp = useScaleApplication()
  const repository = scaleApp.ManualClV1.Repository
  const orchestrator = scaleApp.ManualClV1.Orchestrator
  const poolActivityStore = scaleApp.ManualClV1.PoolActivity
  const poolOhlcStore = scaleApp.ManualClV1.PoolOhlc

  // Local guard that starts as `true` so the very first render shows the
  // "Loading pool…" fallback rather than briefly flashing "Pool not found"
  // between the initial paint and `useEffect` firing. Flipped to `false`
  // once the effect has kicked off the orchestrator fetch.
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    orchestrator.setActiveDetailPoolAddress(poolAddress, scaleApp.Config.chainId)
    poolActivityStore.setActivePool(poolAddress)
    poolOhlcStore.setActivePool(poolAddress)
    setHasMounted(true)

    return () => {
      orchestrator.setActiveDetailPoolAddress(undefined, scaleApp.Config.chainId)
      poolActivityStore.setActivePool(undefined)
      poolOhlcStore.setActivePool(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolAddress])

  const isResolving = !hasMounted || orchestrator.isActiveDetailPoolResolving

  const pool: ManualClPoolStore | undefined = repository.find(new LcAddress(poolAddress))

  if (!pool) {
    return (
      <div
        className={cn(
          "flex min-h-[320px] items-center justify-center rounded-lg border border-p300-light bg-p300 p-8 text-center",
        )}
      >
        <div>
          <h1 className="text-lg font-medium text-t100">{isResolving ? "Loading pool…" : "Pool not found"}</h1>
          <p className="mt-1 text-xs text-t200">
            {isResolving
              ? "Fetching on-chain and indexer data…"
              : "The requested pool could not be located in the indexer."}
          </p>
        </div>
      </div>
    )
  }

  const poolAddressLower = poolAddress.toLowerCase()
  const walletPositionsInPool = scaleApp.ManualClV1.Positions.activePositions.filter(
    (p) => p.pool.address.raw.toLowerCase() === poolAddressLower,
  )

  return (
    <div className="flex flex-col gap-6">
      <IndexerFreshnessBanner />
      <PoolDetailHeaderView pool={pool} />
      <PoolMetricRowView pool={pool} />
      <WalletPositionsInPool poolAddress={pool.address.raw} positions={walletPositionsInPool} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <PoolOhlcChartView
          points={poolOhlcStore.points}
          window={poolOhlcStore.window}
          latestClose={poolOhlcStore.latestClose}
          priceDeltaPct={poolOhlcStore.priceDeltaPct}
          isLoading={poolOhlcStore.isLoading}
          onWindowChange={(next) => poolOhlcStore.setWindow(next)}
          quoteSymbol={pool.token1.symbol}
          baseSymbol={pool.token0.symbol}
        />
        <div className="flex flex-col gap-3 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5">
          <BodyXs className="font-medium uppercase tracking-wide text-t300">Pool info</BodyXs>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <InfoItem label={`${pool.token0.symbol} locked`} value={formatTokenAmount(pool.totalValueLockedToken0)} />
            <InfoItem label={`${pool.token1.symbol} locked`} value={formatTokenAmount(pool.totalValueLockedToken1)} />
            <InfoItem label="Fee tier" value={pool.displayFee} />
            <InfoItem label="Current tick" value={pool.currentTick?.toString() ?? "—"} />
          </dl>
        </div>
      </div>
      <PoolActivityFeedView
        rows={poolActivityStore.rows}
        activeKind={poolActivityStore.activeKind}
        offset={poolActivityStore.offset}
        pageSize={poolActivityStore.pageSize}
        isLoading={poolActivityStore.isLoading}
        error={poolActivityStore.error}
        token0Symbol={pool.token0.symbol}
        token1Symbol={pool.token1.symbol}
        onKindChange={(kind) => poolActivityStore.setActiveKind(kind)}
        onNext={() => poolActivityStore.nextPage()}
        onPrev={() => poolActivityStore.previousPage()}
      />
    </div>
  )
})

const WalletPositionsInPool = observer(function WalletPositionsInPool({
  poolAddress,
  positions,
}: {
  poolAddress: string
  positions: Array<ManualClPositionStore>
}) {
  if (positions.length === 0) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5">
        <BodyXs className="font-medium uppercase tracking-wide text-t300">Your positions</BodyXs>
        <p className="text-sm text-t200">You do not have an active position in this pool yet.</p>
        <Button variant="neutral" asChild size="md" className="w-fit">
          <Link to={`/liquidity/cl/v1/${poolAddress}/create-position`}>Add new position</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-p400-light bg-p400 p-4 lg:p-5">
      <BodyXs className="font-medium uppercase tracking-wide text-t300">Your positions</BodyXs>
      <div className="grid gap-3">
        {positions.map((position) => (
          <PoolPositionCard key={position.tokenId.toString()} poolAddress={poolAddress} position={position} />
        ))}
      </div>
    </div>
  )
})

const PoolPositionCard = observer(function PoolPositionCard({
  poolAddress,
  position,
}: {
  poolAddress: string
  position: ManualClPositionStore
}) {
  const tokenId = position.tokenId.toString()
  const displayId = position.nfpmTokenId?.toString() ?? tokenId
  const apr = position.bestAprDisplay
  const aprLabel = formatAprLabel(apr.label)

  return (
    <div className="rounded-lg border border-p300-light bg-p300 p-3 transition-colors hover:border-primary-400/40">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <DisplayTokens tokens={[position.pool.token0, position.pool.token1]} tokenSize={28} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-t100">Position #{displayId}</span>
              <span
                className={cn(
                  "rounded px-2 py-0.5 text-[10px] font-medium",
                  position.isInRange ? "bg-primary-400/20 text-primary-400" : "bg-error-background/15 text-error-text",
                )}
              >
                {position.isInRange ? "In range" : "Out of range"}
              </span>
            </div>
            <div className="mt-1 text-xs text-t200">{position.displayRange}</div>
          </div>
        </div>

        <div className="flex flex-col gap-2 md:items-end">
          <div className="text-xs">
            <span className="text-t300">{aprLabel}</span>
            {apr.formattedPct ? <span className="ml-2 font-medium text-t100">{apr.formattedPct}</span> : null}
          </div>
          <Button variant="neutral" asChild size="sm">
            <Link to={`/liquidity/cl/v1/${poolAddress}/manage/${tokenId}`}>Manage</Link>
          </Button>
        </div>
      </div>
    </div>
  )
})

function formatAprLabel(label: ManualClPositionStore["bestAprDisplay"]["label"]): string {
  if (label === "Accrued") return "Earned fees APR"
  if (label === "Current") return "Current APR"
  if (label === "Pool") return "Pool APR estimate"
  return label
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wide text-t300">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-t100">{value}</dd>
    </div>
  )
}

function formatTokenAmount(value: number): string {
  if (!Number.isFinite(value) || value === 0) return "0"
  const abs = Math.abs(value)
  const digits = abs >= 1_000 ? 0 : abs >= 1 ? 2 : 4
  return value.toLocaleString(undefined, { maximumFractionDigits: digits })
}
